import { Injectable, signal } from '@angular/core';
import { Observable, of } from 'rxjs';
import { delay } from 'rxjs/operators';

import { PERSONAL_REPORT_SUBSCRIPTIONS_SEED, REPORT_SUBSCRIPTIONS_SEED } from '@common/data';
import type { ReportSubscription } from '@common/types';

const LIST_LATENCY_MS = 260;
const SAVE_LATENCY_MS = 300;
const SEND_LATENCY_MS = 900;

@Injectable({ providedIn: 'root' })
export class ReportSubscriptionsService {
  private readonly subscriptions = signal<ReadonlyArray<ReportSubscription>>([
    ...REPORT_SUBSCRIPTIONS_SEED,
    ...PERSONAL_REPORT_SUBSCRIPTIONS_SEED,
  ]);

  /** What an admin sends from the account; personal subscriptions are their owners' business. */
  forAccount(accountId: string): Observable<ReportSubscription[]> {
    return of(
      this.subscriptions().filter((sub) => sub.accountId === accountId && !sub.ownerUserId),
    ).pipe(delay(LIST_LATENCY_MS));
  }

  /** "Email me this report" subscriptions belonging to one person, across all their accounts. */
  forOwner(userId: string): Observable<ReportSubscription[]> {
    return of(this.subscriptions().filter((sub) => sub.ownerUserId === userId)).pipe(
      delay(LIST_LATENCY_MS),
    );
  }

  toggle(id: string): Observable<void> {
    this.subscriptions.update((list) =>
      list.map((sub) => (sub.id === id ? { ...sub, isEnabled: !sub.isEnabled } : sub)),
    );
    return of(undefined).pipe(delay(SAVE_LATENCY_MS));
  }

  remove(id: string): Observable<void> {
    this.subscriptions.update((list) => list.filter((sub) => sub.id !== id));
    return of(undefined).pipe(delay(SAVE_LATENCY_MS));
  }

  sendNow(id: string): Observable<void> {
    this.subscriptions.update((list) =>
      list.map((sub) =>
        sub.id === id
          ? { ...sub, lastRunIso: new Date().toISOString(), lastRunStatus: 'sent' }
          : sub,
      ),
    );
    return of(undefined).pipe(delay(SEND_LATENCY_MS));
  }

  create(subscription: Omit<ReportSubscription, 'id'>): Observable<ReportSubscription> {
    const created: ReportSubscription = {
      ...subscription,
      id: `${subscription.accountId}-sub-${Date.now().toString(36)}`,
    };
    this.subscriptions.update((list) => [created, ...list]);
    return of(created).pipe(delay(SAVE_LATENCY_MS));
  }
}
